import { RoleOnboardingConfig } from '../../types/onboarding';
import {
  DEMO_APP_ICON,
  DEMO_APP_DESCRIPTION,
  demoVersions,
  createDemoDataForUser,
  getDemoAppNameForRole
} from './demoData';

/**
 * Конфигурация онбординга для Analyst
 */
export const analystOnboardingConfig: RoleOnboardingConfig = {
  role: 'analyst',
  demoAppName: getDemoAppNameForRole('analyst'),
  demoAppIcon: DEMO_APP_ICON,
  demoAppDescription: DEMO_APP_DESCRIPTION,

  steps: [
    // === БЛОК 1: Вступ (об'єднані кроки 1 і 2) ===
    {
      id: 'analyst-welcome',
      target: '[data-tour="user-profile"]',
      title: '👋 Вітаємо, {displayName}!',
      description: 'Цей інтерактивний тур познайомить вас з усіма можливостями платформи. Як аналітик ви маєте повний доступ до всіх функцій: створення та редагування проєктів, управління версіями та макетами, розстановка подій, зміна статусів, коментування та запрошення команди.',
      position: 'bottom-right',
      waitForElement: 500
    },
    
    // === БЛОК 2: Додатки ===
    {
      id: 'analyst-apps-intro',
      target: '[data-tour="app-switcher"]',
      title: '📱 Розділ 1: Додатки',
      description: 'Додаток — це ваш проєкт, в рамках якого ви створюєте версії та макети. Тут відображаються всі ваші додатки: як власник ви бачите додатки, якими володієте, а також додатки, до яких вас запросили як учасника. Кожен додаток містить версії та макети.',
      position: 'right'
    },
    {
      id: 'analyst-create-app',
      target: '[data-tour="create-project"]',
      title: '➕ Створення нового додатку',
      description: 'Натисніть цю кнопку, щоб створити новий додаток. Вам потрібно вказати назву та опис. Ви автоматично станете власником з повними правами на управління.',
      position: 'bottom',
      action: () => {
        // Відкриваємо випадаючий список додатків перед показом кроку
        // Open app dropdown by dispatching custom event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('app-dropdown-change', { detail: { open: true } }));
        }
      }
    },
    // === БЛОК 3: Версії ===
    {
      id: 'analyst-versions-intro',
      target: '[data-tour="version-selector"]',
      title: '📊 Розділ 2: Версії',
      description: 'Версії дозволяють відстежувати еволюцію вашого додатку. Тут відображаються всі версії — активна підсвічена. Клікніть на три крапки поруч з версією для управління (редагування, копіювання, видалення). Демо-проєкт має 2 версії: 1.0 (старий реліз) та 2.0 (поточна версія).',
      position: 'bottom',
      action: () => {
        // Закриваємо випадаючий список додатків перед показом кроку
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('app-dropdown-change', { detail: { open: false } }));
        }
      }
    },
    {
      id: 'analyst-create-version',
      target: '[data-tour="create-version"]',
      title: '🆕 Створення нової версії',
      description: 'Натисніть "+" щоб створити нову версію. Ви можете створити версію з нуля або скопіювати макети з існуючої версії як базу. Це зручно при підготовці до нового релізу.',
      position: 'bottom'
    },
    {
      id: 'analyst-compare-versions',
      target: '[data-tour="compare-versions"]',
      title: '🔍 Порівняння версій',
      description: 'Ця кнопка відкриває режим порівняння двох версій бок-о-бок. Ви можете побачити, які макети додалися, видалилися або змінилися між версіями 1.0 та 2.0.',
      position: 'bottom'
    },

    // === БЛОК 4: Макети ===
    {
      id: 'analyst-screens-intro',
      target: '[data-tour="screens-list"]',
      title: '🖼️ Розділ 3: Макети екранів',
      description: 'Макети — це зображення екранів вашого додатку. Тут відображаються всі макети поточної версії. Натисніть "Режим редагування" (кнопка з олівцем), щоб змінити порядок макетів перетягуванням.',
      position: 'right'
    },
    {
      id: 'analyst-edit-mode',
      target: '[data-tour="edit-mode-button"]',
      title: '✏️ Режим редагування',
      description: 'Натисніть цю кнопку, щоб увімкнути режим редагування. В цьому режимі ви можете перетягувати макети для зміни їх порядку та додавати нові макети.',
      position: 'bottom',
      action: () => {
        // Активуємо режим редагування перед показом кроку
        if (typeof window !== 'undefined') {
          const store = (window as any).__STORE__;
          if (store) {
            store.getState().setEditMode(true);
          }
        }
      }
    },
    {
      id: 'analyst-create-screen',
      target: '[data-tour="create-screen"]',
      title: '📤 Додавання макету',
      description: 'Натисніть "+" щоб завантажити новий макет. Підтримуються формати JPG, PNG, WebP. Макет буде додано до поточної активної версії. Ви можете додати опис та назву.',
      position: 'right'
    },

    // === НОВИЙ КРОК: Зміна розміру меток ===
    {
      id: 'analyst-marker-size',
      target: '[data-tour="marker-size"]',
      title: '🔍 Розмір маркерів',
      description: 'Використовуйте ці кнопки для зміни розміру маркерів подій на макеті. Збільште або зменште масштаб від 10% до 200% для зручності роботи з різними макетами.',
      position: 'bottom'
    },

    // === БЛОК 5: Канва та події ===
    {
      id: 'analyst-canvas-area',
      target: '[data-tour="canvas"]',
      title: '🎨 Розділ 4: Канва та події',
      description: 'Канва — це робоча область, де ви розставляєте події на макетах. Використовуйте колесо миші для масштабування, перетягування для навігації. Подвійний клік скидає масштаб до 100%. У демо-проєкті на макетах розставлено 7-9 подій у різних статусах.',
      position: 'center',
      action: () => {
        // Вимикаємо режим редагування перед показом канви
        if (typeof window !== 'undefined') {
          const store = (window as any).__STORE__;
          if (store) {
            store.getState().setEditMode(false);
          }
        }
      }
    },
    {
      id: 'analyst-context-menu',
      target: '[data-tour="context-menu"]',
      title: '📍 Дії на холсті',
      description: 'Клікніть ПРАВОЮ кнопкою миші на будь-якій точці макета, щоб відкрити меню дій. Тут ви можете створити нову подію, вказавши назву, тип, опис та параметри.',
      position: 'center',
      waitForElement: 800,
      onNext: () => {
        // Відкриваємо форму події при переході до наступного кроку
        setTimeout(() => {
          const contextMenu = document.querySelector('[data-tour="context-menu"]');
          if (contextMenu) {
            const selectors = [
              '[data-tour="context-menu"] button',
              '[data-tour="context-menu"] [role="menuitem"]',
              '[data-tour="context-menu"] li',
              '[data-tour="context-menu"] a',
              '[data-tour="context-menu"] > div > div > div',
              '[data-tour="context-menu"] *'
            ];
            
            for (const selector of selectors) {
              const items = document.querySelectorAll(selector);
              for (const item of items) {
                const text = item.textContent?.toLowerCase().trim() || '';
                if (text.includes('додати подію') ||
                    text.includes('створити подію') ||
                    text.includes('подію') ||
                    text === 'подія' ||
                    text === '+ подія') {
                  (item as HTMLElement).click();
                  console.log('[Tour] Clicked on menu item in onNext:', item.textContent);
                  return;
                }
              }
            }
          }
          // Якщо не знайшли в контекстному меню, пробуємо знайти кнопку створення події на панелі
          const addEventButton = document.querySelector('[data-tour="add-event-button"], button[title*="подію"], button[title*="подія"]');
          if (addEventButton) {
            (addEventButton as HTMLElement).click();
            console.log('[Tour] Clicked on add event button');
          }
        }, 500);
      },
      action: () => {
        // Симулюємо ПКМ клік на канві для відкриття контекстного меню
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            const canvas = document.querySelector('[data-tour="canvas"]');
            if (canvas) {
              // Знаходимо контейнер зображення для кліку - шукаємо img або контейнер зображення
              const imageContainer = canvas.querySelector('img') ||
                                     canvas.querySelector('[class*="relative"]') ||
                                     canvas.querySelector('[class*="canvas"]') ||
                                     canvas as HTMLElement;
              const targetElement = imageContainer as HTMLElement;

              // Отримуємо координати центру елемента
              const rect = targetElement.getBoundingClientRect();
              const x = rect.left + rect.width / 2;
              const y = rect.top + rect.height / 2;

              // Створюємо подію contextmenu з правильними параметрами
              const contextMenuEvent = new MouseEvent('contextmenu', {
                bubbles: true,
                cancelable: true,
                view: window,
                button: 2, // Права кнопка миші
                buttons: 2,
                clientX: x,
                clientY: y,
                screenX: window.screenX + x,
                screenY: window.screenY + y,
                relatedTarget: targetElement
              });

              // Диспатчимо подію на знайдений елемент
              targetElement.dispatchEvent(contextMenuEvent);

              console.log('[Tour] Context menu event dispatched at:', x, y);
            }
          }, 500); // Збільшена затримка для гарантії готовності канви
        }
      }
    },
    // === КРОК 13: Форма створення події (відкривається через "Додати подію") ===
    {
      id: 'analyst-event-modal',
      target: '[data-tour="event-modal"]',
      title: '📝 Форма створення події',
      description: 'Ця форма дозволяє створити нову подію. Заповніть поля: назва події (унікальний ідентифікатор), тип (Click, View, Input тощо), опис (для чого використовується), статус (Нова, Впроваджено тощо), та додайте параметри при необхідності.',
      position: 'left',
      waitForElement: 1000,
      onNext: () => {
        // Закрываем форму создания события при переходе к следующему шагу
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('tour-close-event-modal'));
          console.log('[Tour] Event modal closed via onNext');
        }
      },
      action: () => {
        // Открываем форму напрямую через событие
        if (typeof window !== 'undefined') {
          console.log('[Tour] Dispatching tour-open-event-modal event');
          window.dispatchEvent(new CustomEvent('tour-open-event-modal'));
        }
      }
    },
    // === КРОК 14: Маркери подій ===
    {
      id: 'analyst-event-markers',
      target: '[data-tour="event-marker"]',
      title: '📌 Маркери подій',
      description: 'Кожна подія відображається як маркер з номером. Колір маркера відповідає статусу події. Натисніть на маркер, щоб відкрити деталі події в правій панелі.',
      position: 'right',
      onNext: () => {
        // Закрываем форму создания события если открыта
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('tour-close-event-modal'));
        }
      },
      action: () => {
        // Підсвічуємо тільки перший маркер замість всіх
        setTimeout(() => {
          const markers = document.querySelectorAll('[id^="hotspot-"]');
          if (markers.length > 0) {
            markers[0].classList.add('tour-highlight');
          }
        }, 300);
      }
    },
    // === КРОК 15: Деталі події ===
    {
      id: 'analyst-event-details',
      target: '[data-tour="event-details"]',
      title: '📝 Деталі події',
      description: 'Права панель показує деталі вибраної події: назву, опис, тип, статус, параметри та коментарі. Тільки аналітик може редагувати всі поля події.',
      position: 'left',
      action: () => {
        // Выбираем первое событие для открытия панели деталей
        if (typeof window !== 'undefined') {
          const store = (window as any).__STORE__;
          if (store) {
            // Ищем маркеры событий на макете
            const markers = document.querySelectorAll('[id^="hotspot-"]');
            if (markers.length > 0) {
              const firstMarkerId = markers[0].id.replace('hotspot-', '');
              // Используем правильный метод selectEvent вместо setState
              store.getState().selectEvent(firstMarkerId);
              console.log('[Tour] Selected first event via selectEvent:', firstMarkerId);
              
              // Подсвечиваем выбранный маркер
              setTimeout(() => {
                const selectedMarker = document.querySelector(`[id="hotspot-${firstMarkerId}"]`);
                if (selectedMarker) {
                  selectedMarker.classList.add('tour-highlight');
                }
              }, 100);
            }
          }
        }
      }
    },
    // === КРОК 16: Фільтр "Всі статуси" (оновлений - видалено кроки 17-21) ===
    {
      id: 'analyst-event-status-filter',
      target: '[data-tour="status-filter"]',
      title: '🏷️ Фільтр статусів',
      description: 'Тут ви можете фільтрувати події за статусом. Оберіть один або кілька статусів: 🟢 Нова, 🟡 Змінено, 🔵 Впроваджено, 🔴 На видалення, ⚪ Видалено. Це допомагає швидко знайти події певного статусу.',
      position: 'bottom',
      onNext: () => {
        // Закриваємо дропдаун статусів
        if (typeof window !== 'undefined') {
          const escapeEvent = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true
          });
          document.dispatchEvent(escapeEvent);
        }
        // Вибираємо подію для наступного кроку (Коментарі)
        setTimeout(() => {
          const store = (window as any).__STORE__;
          if (store) {
            const markers = document.querySelectorAll('[id^="hotspot-"]');
            if (markers.length > 0) {
              const firstMarkerId = markers[0].id.replace('hotspot-', '');
              store.getState().selectEvent(firstMarkerId);
              console.log('[Tour] Pre-selected event for comments step:', firstMarkerId);
            }
          }
        }, 200);
      },
      action: () => {
        // Знімаємо підсвічування з усіх маркерів
        if (typeof window !== 'undefined') {
          document.querySelectorAll('.tour-status-highlight, .tour-highlight').forEach(el => {
            el.classList.remove('tour-status-highlight');
            el.classList.remove('tour-highlight');
          });
        }
        // Відкриваємо дропдаун статусів
        setTimeout(() => {
          const statusButton = document.querySelector('[data-tour="event-statuses"] button');
          if (statusButton) {
            (statusButton as HTMLElement).click();
          }
        }, 300);
      }
    },
    // === КРОК 17: Коментарі (був крок 22) ===
    {
      id: 'analyst-event-comments',
      target: '[data-tour="event-comments"]',
      title: '💬 Коментарі',
      description: 'Додавайте коментарі до подій для обговорення з командою. Розробники можуть задавати питання, аналітики — уточнювати вимоги. Всі коментарі зберігаються в історії.',
      position: 'left',
      waitForElement: 800,
      action: () => {
        // Вибираємо подію та підсвічуємо її маркер
        if (typeof window !== 'undefined') {
          const store = (window as any).__STORE__;
          if (store) {
            // Спочатку вибираємо подію
            const markers = document.querySelectorAll('[id^="hotspot-"]');
            if (markers.length > 0) {
              const firstMarkerId = markers[0].id.replace('hotspot-', '');
              store.getState().selectEvent(firstMarkerId);
              console.log('[Tour] Selected event for comments step:', firstMarkerId);
              
              // Додаємо візуальне підсвічування маркера
              setTimeout(() => {
                const selectedMarker = document.querySelector(`[id="hotspot-${firstMarkerId}"]`);
                if (selectedMarker) {
                  selectedMarker.classList.add('tour-highlight');
                  console.log('[Tour] Added highlight to marker:', firstMarkerId);
                }
                
                // Прокручуємо до коментарів
                const commentsSection = document.querySelector('[data-tour="event-comments"]');
                if (commentsSection) {
                  commentsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  console.log('[Tour] Scrolled to comments section');
                }
              }, 400);
            }
          }
        }
      }
    },

    // === БЛОК 6: Фільтри та пошук ===
    // Розділ 5: Фільтри та пошук - ВИДАЛЕНО за запитом користувача
    {
      id: 'analyst-filters',
      target: '[data-tour="filters"]',
      title: '⚡ Фільтри подій',
      description: 'Фільтруйте події за статусом (Нова, Впроваджено тощо) та типом (Click, View тощо). Активні фільтри підсвічуються. Фільтри зберігаються при перемиканні між макетами.',
      position: 'left'
    },
    {
      id: 'analyst-search',
      target: '[data-tour="search"]',
      title: '🔍 Пошук подій',
      description: 'Використовуйте пошук для знаходження подій за назвою або описом. Пошук працює в реальному часі та підсвічує знайдені події на макеті.',
      position: 'left'
    },

    // === БЛОК 7: Команда та доступ ===
    {
      id: 'analyst-app-members',
      target: '[data-tour="app-members"]',
      title: '🔑 Управління доступом',
      description: 'Натисніть цю кнопку, щоб відкрити панель управління учасниками. Тут ви можете запросити нових користувачів за email, змінити їх ролі або видалити з проєкту.',
      position: 'bottom'
    },
    // === КРОК 31: Форма приглашення ===
    {
      id: 'analyst-invite-form',
      target: '[data-tour="invite-form"]',
      title: '✉️ Запросити учасника',
      description: 'Введіть email користувача та оберіть роль (Розробник або Глядач), потім натисніть "Запросити". Користувач отримає запрошення і зможе приєднатися до проєкту.',
      position: 'left',
      action: () => {
        // Відкриваємо модальне вікно учасників
        if (typeof window !== 'undefined') {
          const membersButton = document.querySelector('[data-tour="app-members"]');
          if (membersButton) {
            (membersButton as HTMLElement).click();
          }
        }
      }
    },

    // === БЛОК 8: Додаткові функції ===
    // === КРОК 27: Панель історії ===
    {
      id: 'analyst-history',
      target: '[data-tour="history-panel"]',
      title: '🕐 Історія змін',
      description: 'Панель історії показує всі зміни подій: створення, редагування, зміну статусів. Ви можете побачити хто, коли і що змінив. Корисно для відстеження активності команди.',
      position: 'left',
      waitForElement: 800,
      onNext: () => {
        // Закриваємо панель історії перед переходом до експорту
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('tour-close-history-panel'));
          console.log('[Tour] Closed history panel via onNext');
        }
      },
      action: () => {
        // Закриваємо модальне вікно учасників якщо відкрите
        if (typeof window !== 'undefined') {
          const escapeEvent = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true
          });
          document.dispatchEvent(escapeEvent);
        }
        // Відкриваємо панель історії через подію
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('tour-open-history-panel'));
          console.log('[Tour] Dispatched tour-open-history-panel event');
        }, 300);
      }
    },
    {
      id: 'analyst-export',
      target: '[data-tour="export-panel"]',
      title: '📤 Експорт даних',
      description: 'Експортуйте всі події проєкту в форматі JSON або CSV для імпорту в Firebase Analytics або інші системи. Корисно для резервного копіювання або міграції.',
      position: 'bottom'
    },

    // === БЛОК 9: Завершення ===
    {
      id: 'analyst-completion',
      target: null as any,
      title: '🎉 Вітаємо! Тур завершено',
      description: 'Ви ознайомились з усіма можливостями аналітика! Тепер ви можете створювати власні проєкти, керувати версіями та макетами, розставляти події та запрошувати команду. Спробуйте попрактикуватись на цьому демо-проєкті!',
      position: 'center'
    }
  ],

  demoData: {
    name: getDemoAppNameForRole('analyst'),
    description: DEMO_APP_DESCRIPTION,
    versions: demoVersions
  }
};

/**
 * Получает демо-данные для Analyst с уникальными ID
 * Версии отсортированы: активная версия (2.0) идет первой, затем предыдущие
 * Использует реальные данные из экспорта JSON
 */
export function getAnalystDemoData(userId: string): RoleOnboardingConfig['demoData'] {
  return createDemoDataForUser(userId, 'analyst');
}
